/**
 * RenderQueue - Manages rendering to prevent loops and optimize performance
 * 
 * Uses requestAnimationFrame and dirty checking to batch renders
 * and prevent infinite render loops
 * 
 * @module core/RenderQueue
 */

class RenderQueue {
  constructor() {
    // Map<Component, {priority:number, timestamp:number}>
    this.dirty = new Map();
    this.rendering = false;
    this.frameId = null;
    this.renderCount = 0;
    this.maxRenderPerFrame = 10; // Prevent runaway renders
    
    // Performance monitoring
    this.stats = {
      totalRenders: 0,
      droppedRenders: 0,
      averageRenderTime: 0
    };
  }
  
  /**
   * Mark a component as needing render
   * @param {Object} component - Component with a render() method
   * @param {number} priority - Render priority (higher = sooner)
   */
  markDirty(component, priority = 0) {
    if (!component || typeof component.render !== 'function') {
      console.warn('Invalid component passed to markDirty:', component);
      return;
    }
    
    // Record highest priority per component
    const prev = this.dirty.get(component);
    const newPriority = Math.max(prev?.priority ?? -Infinity, priority);
    this.dirty.set(component, { priority: newPriority, timestamp: performance.now() });
    
    // Schedule render if not already scheduled
    if (!this.rendering && !this.frameId) {
      this.scheduleRender();
    }
  }
  
  /**
   * Schedule a render on the next animation frame
   */
  scheduleRender() {
    if (this.frameId) return; // Already scheduled
    
    this.frameId = requestAnimationFrame(() => {
      this.flush();
    });
  }
  
  /**
   * Flush all dirty components
   */
  flush() {
    if (this.rendering) {
      console.warn('Render already in progress, skipping flush');
      this.stats.droppedRenders++;
      return;
    }
    
    this.rendering = true;
    this.frameId = null;
    const startTime = performance.now();
    
    try {
      // Sort by priority (higher priority first)
      const sortedEntries = Array.from(this.dirty.entries())
        .sort((a, b) => b[1].priority - a[1].priority); // [component, meta]
      
      // Clear map before rendering (components might re-dirty during render)
      this.dirty.clear();
      
      // Render each component with runaway protection
      let renderCount = 0;
      for (const [component] of sortedEntries) {
        if (renderCount >= this.maxRenderPerFrame) {
          console.warn(`Max renders per frame (${this.maxRenderPerFrame}) reached, deferring remaining`);
          // Re-add remaining components as dirty with lower priority
          sortedEntries.slice(renderCount).forEach(([comp, meta]) => {
            this.markDirty(comp, Math.min(-1, meta.priority));
          });
          break;
        }
        
        try {
          // Render the component
          component.render();
          renderCount++;
          this.stats.totalRenders++;
        } catch (error) {
          console.error('Error rendering component:', error, component);
        }
      }
      
      // Update stats
      const renderTime = performance.now() - startTime;
      this.stats.averageRenderTime = 
        (this.stats.averageRenderTime * 0.9) + (renderTime * 0.1); // Weighted average
      
      if (renderTime > 16) { // More than one frame (60fps = 16.67ms)
        console.warn(`Slow render detected: ${renderTime.toFixed(2)}ms`);
      }
      
    } finally {
      this.rendering = false;
      
      // Check if new components were marked dirty during render
      if (this.dirty.size > 0) {
        this.scheduleRender();
      }
    }
  }
  
  /**
   * Cancel all pending renders
   */
  cancelAll() {
    if (this.frameId) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
    this.dirty.clear();
    this.rendering = false;
  }
  
  /**
   * Get render statistics
   */
  getStats() {
    return {
      ...this.stats,
      pendingRenders: this.dirty.size,
      isRendering: this.rendering
    };
  }
  
  /**
   * Reset statistics
   */
  resetStats() {
    this.stats = {
      totalRenders: 0,
      droppedRenders: 0,
      averageRenderTime: 0
    };
  }
}

// Create singleton instance
const renderQueue = new RenderQueue();

// Export both class and instance
export { RenderQueue, renderQueue as default };

// Attach to window in development
if (import.meta.env.DEV) {
  window.RenderQueue = renderQueue;
  console.log('🎨 RenderQueue available at window.RenderQueue');
  console.log('📊 Check stats: window.RenderQueue.getStats()');
}
